import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { BedDouble, CalendarDays, Filter, Pencil, Plus, Users, X, type LucideIcon } from "lucide-react";
import { clsx } from "clsx";
import { api } from "@/lib/api-client";
import { queryClient } from "@/lib/query-client";
import { useAppStore } from "@/store/app-store";
import type { Booking, Room } from "@/types/domain";
import { currency, initials, shortDate } from "@/lib/format";
import { useBackCloseable } from "@/lib/modal-stack";
import { Button } from "@/components/ui/Button";
import { DateInput } from "@/components/ui/DateInput";
import { LoadingGrid, StateView } from "@/components/ui/StateView";
import { FieldError, ResourceDialog, ResourceToolbar, DialogFooter, ListResponse, Req, cleanBody, listItems, optionLabel, selectableOptions } from "@/components/resource/dialog-kit";
import { BookingDetailDialog } from "@/components/resource/booking-detail";
import type { PropertyTax } from "@/features/settings/TaxesSettingsPage";

type ResourceType = "bookings" | "rooms" | "guests";
type RoomTypeOption = { id: string; propertyId: string; categoryId?: string; name: string; pricePerNight?: number | string; taxId?: string | null; maxAdults?: number; maxChildren?: number; floor?: string; description?: string; isActive?: boolean };

const meta: Record<ResourceType, { icon: LucideIcon; title: string; subtitle: string; endpoint: string }> = {
  bookings: { icon: CalendarDays, title: "Bookings", subtitle: "Manage every reservation from request to checkout.", endpoint: "/bookings" },
  rooms: { icon: BedDouble, title: "Rooms", subtitle: "A live view of inventory, readiness and pricing.", endpoint: "/rooms" },
  guests: { icon: Users, title: "Guests", subtitle: "Guest profiles are composed from your booking history.", endpoint: "/bookings" },
};

const filterFieldByType: Partial<Record<ResourceType, string>> = { bookings: "bookingStatus", rooms: "physicalStatus" };
const filterOptionsByType: Partial<Record<ResourceType, { value: string; label: string }[]>> = {
  bookings: [
    { value: "", label: "All statuses" },
    { value: "PENDING", label: "Pending" },
    { value: "CONFIRMED", label: "Confirmed" },
    { value: "CHECKED_IN", label: "Checked in" },
    { value: "COMPLETED", label: "Completed" },
    { value: "CANCELLED", label: "Cancelled" },
    { value: "REJECTED", label: "Rejected" },
    { value: "EXPIRED", label: "Expired" },
    { value: "AUTO_CANCELLED", label: "Auto-cancelled" },
  ],
  rooms: [
    { value: "", label: "All statuses" },
    { value: "AVAILABLE", label: "Available" },
    { value: "OCCUPIED", label: "Occupied" },
    { value: "RESERVED", label: "Reserved" },
    { value: "MAINTENANCE", label: "Maintenance" },
  ],
};

const bookingQuickFilters = [
  { value: "", label: "All" },
  { value: "CONFIRMED", label: "Confirmed" },
  { value: "CHECKED_IN", label: "Checked-in" },
  { value: "PENDING", label: "Pending" },
];

const roomQuickFilters = [
  { value: "", label: "All" },
  { value: "AVAILABLE", label: "Available" },
  { value: "OCCUPIED", label: "Occupied" },
  { value: "MAINTENANCE", label: "Cleaning" },
];

const roomStatusMeta: Record<Room["physicalStatus"], { label: string; key: string }> = {
  AVAILABLE: { label: "Available", key: "available" },
  RESERVED: { label: "Checked-in", key: "checked-in" },
  OCCUPIED: { label: "Occupied", key: "occupied" },
  MAINTENANCE: { label: "Cleaning", key: "cleaning" },
};

const AVATAR_PALETTE = [
  { bg: "#ffe9d9", fg: "#c8631f" },
  { bg: "#dbe9fb", fg: "#3468b0" },
  { bg: "#fbdfe4", fg: "#c3467a" },
  { bg: "#e1f3ea", fg: "#1f8f6b" },
];

function avatarColor(seed: string) {
  const code = seed.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return AVATAR_PALETTE[code % AVATAR_PALETTE.length];
}

function bookingSearchText(booking: Booking) {
  const rooms = (booking.allottedRooms ?? []).flatMap((stay) => [stay.room?.name, stay.room?.roomNumber]);
  return [booking.guestName, booking.guestEmail, booking.bookingRef, booking.room?.name, booking.room?.roomNumber, booking.roomType?.name, ...rooms]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function bookingStatusLabel(status: Booking["bookingStatus"]) {
  return status.split("_").map((word) => word[0] + word.slice(1).toLowerCase()).join("-");
}

const roomTypeFilterOptions = [
  { value: "", label: "All room types" },
  { value: "SINGLE", label: "Single" },
  { value: "DOUBLE", label: "Double" },
  { value: "DORM", label: "Dorm" },
  { value: "SUITE", label: "Suite" },
  { value: "FAMILY", label: "Family" },
  { value: "HALL", label: "Hall" },
  { value: "OTHER", label: "Other" },
];

const roomTypeLabels: Record<string, string> = Object.fromEntries(roomTypeFilterOptions.filter((option) => option.value).map((option) => [option.value, option.label]));

export function ResourcePage({ type }: { type: ResourceType }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [roomTypeFilter, setRoomTypeFilter] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  useBackCloseable(filterOpen, () => setFilterOpen(false));
  const [dialog, setDialog] = useState<ResourceType | null>(null);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const isOwner = useAppStore((state) => state.user?.role === "OWNER");
  const isAdmin = useAppStore((state) => state.user?.role === "ADMIN");
  const role = useAppStore((state) => state.user?.role);
  const propertyId = useAppStore((state) => state.activeProperty!.id);
  const current = meta[type];
  const filterField = filterFieldByType[type];
  const query = useQuery({ queryKey: [type, propertyId], queryFn: () => api<unknown>(`${current.endpoint}?propertyId=${propertyId}`) });
  const items = useMemo(() => {
    const raw = listItems<unknown>(query.data);
    const unique = type === "guests" ? Array.from(new Map((raw as Booking[]).map((b) => [b.guestPhone, b])).values()) : raw;
    const term = search.trim().toLowerCase();
    const digits = term.replace(/\D/g, "");
    return unique.filter((item) => {
      // A guest is only its name/phone/email - matching the whole booking JSON let any room, city,
      // status or id text match every guest.
      // Bookings likewise match only guest / ref / room text - stringifying each full booking (payments,
      // services, guests, rooms...) on every keystroke made search lag on large lists.
      const matchesSearch = type === "guests"
        ? !term
          || [(item as Booking).guestName, (item as Booking).guestEmail].some((value) => value?.toLowerCase().includes(term))
          || (digits.length > 0 && ((item as Booking).guestPhone ?? "").replace(/\D/g, "").includes(digits))
        : type === "bookings"
          ? !term || bookingSearchText(item as Booking).includes(term) || (digits.length > 0 && ((item as Booking).guestPhone ?? "").replace(/\D/g, "").includes(digits))
          : JSON.stringify(item).toLowerCase().includes(term);
      const matchesFilter = !statusFilter || !filterField || (item as Record<string, unknown>)[filterField] === statusFilter;
      if (!matchesSearch || !matchesFilter) return false;
      if (type === "bookings") {
        const checkIn = new Date((item as Booking).checkInDate).toISOString().slice(0, 10);
        if (dateFrom && checkIn < dateFrom) return false;
        if (dateTo && checkIn > dateTo) return false;
      }
      if (type === "rooms") {
        const room = item as Room;
        if (roomTypeFilter && room.roomType !== roomTypeFilter) return false;
        const price = Number(room.basePrice);
        if (priceMin && price < Number(priceMin)) return false;
        if (priceMax && price > Number(priceMax)) return false;
      }
      return true;
    });
  }, [query.data, search, statusFilter, filterField, type, dateFrom, dateTo, roomTypeFilter, priceMin, priceMax]);
  const Icon = current.icon;
  const addable = type === "rooms" && isAdmin;
  const availableRoomCount = type === "rooms" ? (items as Room[]).filter((room) => room.physicalStatus === "AVAILABLE").length : 0;
  const countLabel = type === "bookings"
    ? `${items.length} reservation${items.length === 1 ? "" : "s"} found`
    : type === "rooms"
    ? `${items.length} room${items.length === 1 ? "" : "s"} · ${availableRoomCount} available`
    : `${items.length} guest${items.length === 1 ? "" : "s"}`;
  const hasActiveFilter = Boolean(search || statusFilter || dateFrom || dateTo || roomTypeFilter || priceMin || priceMax);
  const clearFilters = () => { setSearch(""); setStatusFilter(""); setDateFrom(""); setDateTo(""); setRoomTypeFilter(""); setPriceMin(""); setPriceMax(""); };

  return (
    <main className={clsx("page resource-page", type === "bookings" && "resource-page--bookings", type === "rooms" && "resource-page--rooms")}>
      <section className="resource-head">
        <span className="eyebrow"><Icon size={14} /> {current.title}</span>
        {addable && <Button className="fab-button" onClick={() => setDialog(type)}><Plus size={18} /> <span className="fab-button__label">Add room</span></Button>}
      </section>
      {(type === "bookings" || type === "rooms") && (
        <div className="booking-filter-tabs">
          {(type === "bookings" ? bookingQuickFilters : roomQuickFilters).map((option) => (
            <button key={option.value} type="button" className={clsx("filter-tab", statusFilter === option.value && "filter-tab--active")} onClick={() => setStatusFilter(option.value)}>
              {option.label}
            </button>
          ))}
        </div>
      )}
      {type === "guests" && (
        // Guests has nothing else to filter by (no status dropdown, no extra fields), so the search
        // box is shown right here instead of behind the Filters drawer - hidden inside that drawer,
        // typing never showed live results because the drawer itself covers the list underneath.
        <ResourceToolbar search={search} onSearchChange={setSearch} placeholder="Search guests by name or phone..." onClear={() => setSearch("")} />
      )}
      {!query.isLoading && !query.isError && (
        <div className="reservations-count-row">
          <span>{countLabel}</span>
          {type !== "guests" && (
            <button type="button" className={clsx("filter-trigger", hasActiveFilter && "filter-trigger--active")} aria-label="Filters" onClick={() => setFilterOpen(true)}>
              <Filter size={18} />
            </button>
          )}
        </div>
      )}
      {filterOpen && type !== "guests" && (
        <div className="drawer-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setFilterOpen(false)}>
          <section className="drawer-panel" role="dialog" aria-modal="true">
            <header className="drawer-panel__header">
              <div><span className="eyebrow"><Filter size={14} /> Filters</span><h2>{current.title}</h2></div>
              <button type="button" className="icon-button" aria-label="Close" onClick={() => setFilterOpen(false)}><X /></button>
            </header>
            <div className="drawer-panel__body">
              <ResourceToolbar search={search} onSearchChange={setSearch} placeholder={type === "bookings" ? "Search guest or room..." : `Search ${type} by name...`} filter={statusFilter} onFilterChange={setStatusFilter} filterOptions={filterOptionsByType[type]} onClear={clearFilters}>
                {type === "bookings" && (
                  <div className="date-range-filter">
                    <DateInput aria-label="Check-in from" className="filter-select" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
                    <span>to</span>
                    <DateInput aria-label="Check-in to" className="filter-select" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
                  </div>
                )}
                {type === "rooms" && (
                  <>
                    <select className="filter-select" aria-label="Room type" value={roomTypeFilter} onChange={(event) => setRoomTypeFilter(event.target.value)}>
                      {roomTypeFilterOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    <div className="price-range-filter">
                      <input type="number" min={0} inputMode="numeric" aria-label="Minimum price" placeholder="Min ₹" value={priceMin} onChange={(event) => setPriceMin(event.target.value)} />
                      <span>-</span>
                      <input type="number" min={0} inputMode="numeric" aria-label="Maximum price" placeholder="Max ₹" value={priceMax} onChange={(event) => setPriceMax(event.target.value)} />
                    </div>
                  </>
                )}
              </ResourceToolbar>
            </div>
          </section>
        </div>
      )}
      {query.isLoading ? <LoadingGrid /> : query.isError ? (
        <StateView title={`Couldn't load ${type}`} message="Check the backend connection and try once more." action={() => void query.refetch()} />
      ) : !items.length ? (
        hasActiveFilter
          ? <StateView icon={Icon} title={`No matching ${type}`} message="Try a different search or clear the filters." />
          : <StateView icon={Icon} title={`No ${type} yet`} message="Everything you add will be organized here automatically." />
      ) : (
        <section className="resource-grid">
          {items.map((item, index) => <ResourceCard key={(item as { id?: string }).id ?? index} type={type} item={item} canEdit={isOwner || isAdmin} onViewBooking={setSelectedBooking} onEditRoom={setEditingRoom} />)}
        </section>
      )}
      {selectedBooking && <BookingDetailDialog booking={selectedBooking} role={role ?? "OWNER"} onClose={() => setSelectedBooking(null)} />}
      {dialog === "rooms" && <RoomDialog mode="create" onClose={() => setDialog(null)} />}
      {editingRoom && <RoomDialog mode="edit" room={editingRoom} onClose={() => setEditingRoom(null)} />}
    </main>
  );
}

function ResourceCard({ type, item, canEdit = false, onViewBooking, onEditRoom }: { type: ResourceType; item: unknown; canEdit?: boolean; onViewBooking?: (booking: Booking) => void; onEditRoom?: (room: Room) => void }) {
  if (type === "bookings") {
    const booking = item as Booking;
    const palette = avatarColor(booking.guestName || "Guest");
    const roomName = booking.room?.name ?? "Room";
    const roomTypeName = booking.roomType?.name ?? booking.room?.roomType;
    return (
      <article className="resource-card booking-card" role="button" tabIndex={0} onClick={() => onViewBooking?.(booking)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") onViewBooking?.(booking); }}>
        <span className="avatar-circle" style={{ background: palette.bg, color: palette.fg }}>{initials(booking.guestName)}</span>
        <div className="resource-card__body">
          <h3 className="booking-card__name">{booking.guestName}</h3>
          <div className="resource-card__line2"><span>{roomName}</span>{roomTypeName && <><span className="dot">•</span><span>{roomTypeName}</span></>}</div>
          <div className="resource-card__line3">{shortDate(booking.checkInDate)} – {shortDate(booking.checkOutDate)}</div>
        </div>
        <div className="booking-card__right">
          <strong className="resource-card__value">{currency.format(Number(booking.totalAmount))}</strong>
          <span className={`status status--${booking.bookingStatus.toLowerCase()}`}>{bookingStatusLabel(booking.bookingStatus)}</span>
        </div>
      </article>
    );
  }
  if (type === "rooms") {
    const room = item as Room;
    const status = roomStatusMeta[room.physicalStatus];
    const guestCount = room.capacityAdults + room.capacityChildren;
    return (
      <article className="resource-card room-card">
        <span className="room-card__number"><strong>{room.roomNumber || "—"}</strong><small>Room</small></span>
        <div className="resource-card__body">
          <div className="resource-card__line1">
            <h3>{room.name}</h3>
            <span className={`room-status room-status--${status.key}`}><i />{status.label}</span>
          </div>
          <div className="resource-card__line2"><span>{roomTypeLabels[room.roomType] ?? room.roomType}</span><span className="dot">•</span><span>{guestCount} guest{guestCount === 1 ? "" : "s"}</span></div>
          <strong className="room-card__price">{currency.format(Number(room.basePrice))}<small> / night</small></strong>
        </div>
        {canEdit && <button type="button" className="property-edit-button" aria-label={`Edit ${room.name}`} onClick={() => onEditRoom?.(room)}><Pencil size={16} /></button>}
      </article>
    );
  }
  const value = item as Record<string, string>;
  return (
    <article className="resource-card">
      <span className="card-icon"><Users /></span>
      <div className="resource-card__body">
        <h3>{value.guestName || "Guest"}</h3>
        <div className="resource-card__line2"><span>{value.guestPhone || "No phone on file"}</span></div>
      </div>
    </article>
  );
}

type RoomForm = {
  roomTypeId: string;
  name: string;
  roomType: "SINGLE" | "DOUBLE" | "DORM" | "SUITE" | "FAMILY" | "HALL" | "OTHER";
  roomNumber: string;
  title: string;
  floor: string;
  physicalStatus: "AVAILABLE" | "OCCUPIED" | "RESERVED" | "MAINTENANCE";
  capacityAdults: number;
  capacityChildren: number;
  basePrice?: number;
  extraBedPrice?: number;
  taxId: string;
  description: string;
  isClean: boolean;
  isBookable: boolean;
  showOnWebsite: boolean;
};

function RoomDialog({ mode, room, onClose }: { mode: "create" | "edit"; room?: Room; onClose: () => void }) {
  const propertyId = useAppStore((state) => state.activeProperty!.id);
  const roomTypes = useQuery({ queryKey: ["room-types", propertyId], queryFn: () => api<ListResponse<RoomTypeOption>>(`/room-types?propertyId=${propertyId}&limit=100`) });
  const taxes = useQuery({ queryKey: ["taxes", propertyId], queryFn: () => api<ListResponse<PropertyTax>>(`/taxes?propertyId=${propertyId}&limit=100`) });
  const roomTypeIdDefault = room ? (room as Room & { roomTypeId?: string; enterpriseRoomType?: { id: string } }).roomTypeId ?? (room as Room & { enterpriseRoomType?: { id: string } }).enterpriseRoomType?.id ?? "" : "";
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<RoomForm>({
    defaultValues: {
      roomTypeId: roomTypeIdDefault,
      name: room?.name ?? "",
      roomType: (room?.roomType as RoomForm["roomType"]) ?? "OTHER",
      roomNumber: room?.roomNumber ?? "",
      title: room?.title ?? "",
      floor: room?.floor ?? "",
      physicalStatus: room?.physicalStatus ?? "AVAILABLE",
      capacityAdults: room?.capacityAdults ?? 1,
      capacityChildren: room?.capacityChildren ?? 0,
      basePrice: room ? Number(room.basePrice) : undefined,
      extraBedPrice: room?.extraBedPrice === undefined ? undefined : Number(room.extraBedPrice),
      taxId: room?.taxId ?? "",
      description: room?.description ?? "",
      isClean: room?.isClean ?? true,
      isBookable: room?.isBookable ?? true,
      showOnWebsite: room?.showOnWebsite ?? true,
    },
  });
  const roomTypeId = watch("roomTypeId");
  const availableRoomTypes = selectableOptions(listItems<RoomTypeOption>(roomTypes.data), roomTypeId);
  const taxId = watch("taxId");
  const taxOptions = selectableOptions(listItems<PropertyTax>(taxes.data), taxId);
  const selectedRoomType = availableRoomTypes.find((item) => item.id === roomTypeId);
  const selectRoomType = (nextRoomTypeId: string) => {
    const selected = availableRoomTypes.find((item) => item.id === nextRoomTypeId);
    setValue("roomTypeId", nextRoomTypeId);
    if (selected?.pricePerNight !== undefined) setValue("basePrice", Number(selected.pricePerNight));
    setValue("taxId", selected?.taxId ?? "");
    if (selected?.maxAdults !== undefined) setValue("capacityAdults", selected.maxAdults);
    if (selected?.maxChildren !== undefined) setValue("capacityChildren", selected.maxChildren);
    setValue("floor", selected?.floor ?? "");
    setValue("description", selected?.description ?? "");
  };
  const linkedFromRoomType = Boolean(selectedRoomType);
  const mutation = useMutation({
    mutationFn: (data: RoomForm) => {
      const body = cleanBody({ ...data, stayProfileId: propertyId }, mode === "edit" ? ["stayProfileId"] : []);
      if (selectedRoomType) {
        if (selectedRoomType.pricePerNight !== undefined) body.basePrice = Number(selectedRoomType.pricePerNight);
        body.taxId = selectedRoomType.taxId ?? null;
        if (selectedRoomType.maxAdults !== undefined) body.capacityAdults = selectedRoomType.maxAdults;
        if (selectedRoomType.maxChildren !== undefined) body.capacityChildren = selectedRoomType.maxChildren;
        body.floor = selectedRoomType.floor ?? "";
        body.description = selectedRoomType.description ?? "";
      } else if (!data.taxId) {
        body.taxId = null;
      }
      return api<Room>(mode === "create" ? "/rooms" : `/rooms/${room!.id}`, {
        method: mode === "create" ? "POST" : "PATCH",
        body,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["rooms"] });
      onClose();
    },
  });

  return (
    <ResourceDialog title={`${mode === "create" ? "Add" : "Edit"} room`} eyebrow="Room details" onClose={onClose}>
      <form noValidate onSubmit={handleSubmit((data) => mutation.mutate(data))}>
        <div className="property-form-grid">
          <label className="property-form-grid__wide">Room type<select value={roomTypeId} onChange={(event) => selectRoomType(event.target.value)}><option value="">No linked room type</option>{availableRoomTypes.map((item) => <option key={item.id} value={item.id}>{optionLabel(item.name, item.isActive)}</option>)}</select></label>
          <label><span className="label-title">Room name<Req /></span>
            <input {...register("name", { required: "Room name is required" })} />
            <FieldError error={errors.name} />
          </label>
          <label>Bed type<select {...register("roomType")}><option value="SINGLE">Single</option><option value="DOUBLE">Double</option><option value="DORM">Dorm</option><option value="SUITE">Suite</option><option value="FAMILY">Family</option><option value="HALL">Hall</option><option value="OTHER">Other</option></select></label>
          <label>Room number<input {...register("roomNumber")} /></label>
          <label>Display title<input {...register("title")} /></label>
          <label>Floor<input {...register("floor")} disabled={linkedFromRoomType} title={linkedFromRoomType ? "Auto-filled from the linked room type" : undefined} /></label>
          <label>Status<select {...register("physicalStatus")}><option value="AVAILABLE">Available</option><option value="OCCUPIED">Occupied</option><option value="RESERVED">Reserved</option><option value="MAINTENANCE">Maintenance</option></select></label>
          <label><span className="label-title">Adult capacity<Req /></span>
            <input type="number" {...register("capacityAdults", { required: "Adult capacity is required", valueAsNumber: true, min: { value: 1, message: "Must be at least 1" } })} disabled={linkedFromRoomType} title={linkedFromRoomType ? "Auto-filled from the linked room type" : undefined} />
            <FieldError error={errors.capacityAdults} />
          </label>
          <label><span className="label-title">Children capacity<Req /></span>
            <input type="number" {...register("capacityChildren", { required: "Children capacity is required", valueAsNumber: true, min: { value: 0, message: "Must be 0 or more" } })} disabled={linkedFromRoomType} title={linkedFromRoomType ? "Auto-filled from the linked room type" : undefined} />
            <FieldError error={errors.capacityChildren} />
          </label>
          <label><span className="label-title">Base price{!selectedRoomType && <Req />}</span>
            <input step="0.01" type="number" {...register("basePrice", {
              valueAsNumber: true,
              validate: (value) => Boolean(selectedRoomType) || (value !== undefined && !Number.isNaN(value) && value > 0) || "Base price is required when no room type is linked",
            })} disabled={linkedFromRoomType} title={linkedFromRoomType ? "Auto-filled from the linked room type" : undefined} />
            <FieldError error={errors.basePrice} />
          </label>
          <label>Extra bed price
            <input step="0.01" type="number" {...register("extraBedPrice", { valueAsNumber: true, min: { value: 0, message: "Must be 0 or more" } })} />
            <FieldError error={errors.extraBedPrice} />
          </label>
          <label>Tax<select {...register("taxId")} disabled={linkedFromRoomType} title={linkedFromRoomType ? "Auto-filled from the linked room type" : undefined}>
            <option value="">No tax</option>
            {taxOptions.map((tax) => <option key={tax.id} value={tax.id}>{optionLabel(`${tax.name} (${Number(tax.percentage).toFixed(2)}%)`, tax.isActive)}</option>)}
          </select></label>
          <label className="property-form-grid__wide">Description<textarea rows={3} {...register("description")} disabled={linkedFromRoomType} title={linkedFromRoomType ? "Auto-filled from the linked room type" : undefined} /></label>
          <div className="property-form-grid__wide room-options">
            <label className="check-option"><input type="checkbox" {...register("isClean")} /><span>Room is clean</span></label>
            <label className="check-option"><input type="checkbox" {...register("isBookable")} /><span>Accept bookings</span></label>
            <label className="check-option"><input type="checkbox" {...register("showOnWebsite")} /><span>Show on website</span></label>
          </div>
        </div>
        {mutation.error && <div className="form-error">{mutation.error.message}</div>}
        <DialogFooter onClose={onClose} loading={mutation.isPending} label={mode === "create" ? "Create room" : "Save changes"} />
      </form>
    </ResourceDialog>
  );
}
